import { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useViewport } from "../../hooks/useViewport";
import { useAppSelector } from "../../store/hooks";
import ModuleSidebar, { type ModuleSidebarSection } from "./ModuleSidebar";

/** Какво получава всяка страница на модула през `useOutletContext`. */
export type ModuleOutletContext = {
  isNarrow: boolean;
  onOpenMobileMenu: () => void;
};

// Точно този път или под него — `/reservations/table` не е `/reservations/tables`.
const isOnPage = (pathname: string, pagePath: string) =>
  pathname === pagePath || pathname.startsWith(`${pagePath}/`);

/**
 * Модул със страници по право и страничното меню вляво (Конфигурация,
 * Резервации). Менюто показва само страниците, за които човек има право — с
 * едно право вижда една страница. Редът на страниците е и редът на
 * fallback-а „първата разрешена": първата страница е индексът на модула
 * (`path === rootPath`), а route-овете ѝ в main.tsx носят същите права.
 */
const ModuleSidebarLayout = ({
  rootPath,
  moduleLabel,
  sections,
}: {
  rootPath: string;
  moduleLabel: string;
  sections: ModuleSidebarSection[];
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { isNarrow, isTablet } = useViewport();
  const location = useLocation();
  const navigate = useNavigate();
  const permissions = useAppSelector((state) => state.auth.permissions);
  const hasPermission = (name: string) => permissions.some((permission) => permission.name === name);

  useEffect(() => {
    if (isTablet && !isNarrow) setCollapsed(true);
    if (!isTablet && !isNarrow) setCollapsed(false);
  }, [isTablet, isNarrow]);

  useEffect(() => {
    if (isNarrow) setMobileOpen(false);
  }, [location.pathname, isNarrow]);

  const pages = sections.flatMap((section) => section.pages);
  const currentPage =
    pages.find((page) => page.path !== rootPath && isOnPage(location.pathname, page.path)) ?? pages[0];

  // Без право за текущата страница → към първата разрешена, ПРЕДИ child
  // AuthorizedRoute да изхвърли потребителя извън модула.
  if (!hasPermission(currentPage.permission)) {
    const firstAllowed = pages.find((page) => hasPermission(page.permission));
    if (firstAllowed && firstAllowed.path !== currentPage.path) {
      return <Navigate to={firstAllowed.path} replace />;
    }
  }

  const allowedSections = sections
    .map((section) => ({ ...section, pages: section.pages.filter((page) => hasPermission(page.permission)) }))
    .filter((section) => section.pages.length > 0);

  const context: ModuleOutletContext = {
    isNarrow,
    onOpenMobileMenu: () => setMobileOpen(true),
  };

  return (
    <div className="flex h-dvh overflow-hidden bg-canvas font-pos-sans text-ink">
      {isNarrow && mobileOpen && (
        <div onClick={() => setMobileOpen(false)} className="fixed inset-0 z-40 bg-[rgba(17,17,17,0.4)]" />
      )}

      <ModuleSidebar
        moduleLabel={moduleLabel}
        sections={allowedSections}
        activePath={currentPage.path}
        onSelect={(path) => navigate(path)}
        collapsed={collapsed}
        onToggle={() => setCollapsed((value) => !value)}
        isNarrow={isNarrow}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />

      <main className="min-h-0 min-w-0 flex-1 overflow-y-auto">
        <Outlet context={context} />
      </main>
    </div>
  );
};

export default ModuleSidebarLayout;
